import React, { useEffect, useRef, useState } from 'react';
import { useDetectClickOutside } from 'react-detect-click-outside';
import ReactList from 'react-list';
import { FiSearch } from 'react-icons/fi';
import { MdTune } from 'react-icons/md';
import { searchActions } from 'actions/search';
import { SearchDetails } from 'models/SearchDetails';
import { SelectModel } from 'models/SelectModel';
import SearchResultList from 'components/SearchResultsList/index';
import { IoCloseCircle } from 'react-icons/io5';
import './style.scss';

const searchOptions: SelectModel[] = [
  { key: 'network', label: 'Network' },
  { key: 'current-layer', label: 'Current Layer' }
];

interface SearchData {
  type: string;
  selectedKey: string;
  keyword: string;
  category: string;
  endpoint?: string;
}

interface Props {
  isNodeSelected: () => boolean;
  searchContentData: SearchDetails;
  onSelectSearchDetailsItem: (item: string) => void;
  onSelectSearchDetailsType: (type: string) => void;
  searchCurrentLayer?: (keyword: string) => Promise<string[]>;
  onSearchResultsChange?: (items: string[]) => void;
  onSearchBlur?: () => void;
  tenantName?: string;
}

const SearchContainer: React.FC<Props> = ({
  isNodeSelected,
  searchContentData,
  onSelectSearchDetailsItem,
  onSelectSearchDetailsType,
  searchCurrentLayer,
  onSearchResultsChange,
  onSearchBlur,
  tenantName,
}) => {
  const [isSearchFocused, setSearchFocused] = useState(false);
  const [isSearchFilterOpened, setSearchFilterOpened] = useState(false);
  const [searchContent, setSearchContent] = useState<string>('');
  const [searchData, setSearchData] = useState<SearchData>();
  const [totalItems, setTotalItems] = useState<number>(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const DEBOUNCE_MS = 3000;

  const triggerSearch = (keyword: string) => {
    setSearchData({
      type: searchContentData.type ?? '',
      selectedKey: searchContentData.selectedKey ?? '',
      keyword: keyword.trim().toUpperCase(),
      category: searchContentData.selectedCategory ?? '',
      endpoint: searchContentData.selectedEndPoint,
    });
    setSearchFocused(true);
  };

  // Clear search input and result count when the checked asset/layer changes
  useEffect(() => {
    setSearchContent('');
    setTotalItems(0);
    setSearchData(undefined);
    onSearchResultsChange?.([]);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = undefined;
    }
  }, [searchContentData.selectedKey, searchContentData.selectedCategory, onSearchResultsChange]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const focusSearchContainer = (e: any) => {
    e.preventDefault();
    setSearchFocused(true);
  };

  const blurSearchContainer = () => {
    setSearchFilterOpened(false);
    setSearchFocused(false);
    onSearchBlur?.();
  };

  const toggleSearchFilter = (e: any) => {
    e.preventDefault();
    setSearchFocused(true);
    setSearchFilterOpened(!isSearchFilterOpened);
  };

  const onChangeSearchContent = (event: any) => {
    const value = event.target.value;
    setSearchContent(value);
    setSearchData(undefined);

    if (!value.trim()) {
      setTotalItems(0);
      onSearchResultsChange?.([]);
    }

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = undefined;
      if (value.trim()) triggerSearch(value);
    }, DEBOUNCE_MS);
  };

  const onKeyDownPressed = (event: any) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = undefined;
      }
      triggerSearch(searchContent);
    }
  };

  const onSearchButtonClicked = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = undefined;
    }
    triggerSearch(searchContent);
  };

  const getSearchOptionClass = (searchOptionKey: string) => {
    if (searchOptionKey === 'current-layer' && isNodeSelected() === false) {
      return 'disabled';
    } else if (searchOptionKey === searchContentData.type) {
      return 'selected';
    } else {
      return '';
    }
  };

  const renderSearchOptions = () => {
    return (
      <ul>
        {searchOptions.map((searchOption) => (
          <li
            className={getSearchOptionClass(searchOption.key)}
            key={searchOption.key}
            onClick={() => onSearchOptionClicked(searchOption.key)}
          >
            <span>{searchOption.label}</span>
          </li>
        ))}
      </ul>
    );
  };

  const onSearchOptionClicked = (key: string) => {
    setSearchFilterOpened(false);
    onSelectSearchDetailsType(key);
  };

  const refSearchContainer = useDetectClickOutside({
    onTriggered: blurSearchContainer,
  });

  const trimmedTenantName = tenantName?.trim();
  const searchPlaceholder =
    trimmedTenantName && trimmedTenantName.length > 0
      ? `Search ${trimmedTenantName}...`
      : 'Search';

  return (
    <div
      className={`xa-search-container${isSearchFocused ? ' focused' : ''}`}
      ref={refSearchContainer}
    >
      <FiSearch className="xa-search-icon" onClick={onSearchButtonClicked} />
      <input
        type="search"
        id="search"
        placeholder={searchPlaceholder}
        value={searchContent}
        onClick={focusSearchContainer}
        onKeyDown={onKeyDownPressed}
        onChange={onChangeSearchContent}
      />
      {searchContent.length > 0 && (
        <IoCloseCircle
          className="xa-search-clear"
          onClick={() => {
            setSearchContent('');
            setSearchData(undefined);
            setTotalItems(0);
            onSearchResultsChange?.([]);
            if (debounceTimerRef.current) {
              clearTimeout(debounceTimerRef.current);
              debounceTimerRef.current = undefined;
            }
          }}
        />
      )}
      {!isSearchFocused && totalItems > 0 && (
        <span className="xa-search-results-count">{totalItems}</span>
      )}
      <span className="xa-search-results-category">
        {searchContentData.selectedCategory === 'Accidents'
          ? 'Accidents: '
          : ''}{' '}
        {searchContentData.selectedLabel}
      </span>
      <MdTune
        className="xa-search-filter-toggle"
        onClick={toggleSearchFilter}
      />
      <div
        className={`xa-search-filter${isSearchFilterOpened ? ' opened' : ''}`}
      >
        <div className="xa-search-filter-title">Search on:</div>
        {renderSearchOptions()}
      </div>
      {isSearchFocused && (
        <SearchResultList
          keyword={searchData?.keyword}
          type={searchData?.type}
          selectedKey={searchData?.selectedKey}
          category={searchData?.category}
          endpoint={searchData?.endpoint}
          searchCurrentLayer={searchCurrentLayer}
          setSearchContent={setSearchContent}
          setSearchFocused={setSearchFocused}
          onSelectSearchDetailsItem={onSelectSearchDetailsItem}
          setTotalItems={setTotalItems}
          onSearchResultsChange={onSearchResultsChange}
        />
      )}
    </div>
  );
};

export default SearchContainer;
